import React, { useState, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Mail, Lock, User, Chrome, Phone, Camera } from 'lucide-react';
import { Logo } from '@/components/Header';
import { toast } from 'sonner';
import { isAcceptedPhoto, PHOTO_ACCEPT } from '@/lib/photoFiles';
import { translateServerError } from '@/lib/serverErrors';
import { safeRedirect, withRedirect, rememberAuthRedirect } from '@/lib/authRedirect';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const RegisterPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  // The listing the traveller was booking, if any (allowed Ridalo pages only).
  const redirect = safeRedirect(searchParams.get('redirect'));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState(searchParams.get('role') || 'client');
  const [whatsapp, setWhatsapp] = useState('');
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const avatarInputRef = useRef(null);
  const { register, googleLogin } = useAuth();
  const navigate = useNavigate();

  // SEO: Prevent indexing of registration page
  React.useEffect(() => {
    document.title = 'Sign Up | Ridalo';
    const metaRobots = document.querySelector('meta[name="robots"]') || document.createElement('meta');
    metaRobots.setAttribute('name', 'robots');
    metaRobots.setAttribute('content', 'noindex, nofollow');
    if (!document.querySelector('meta[name="robots"]')) {
      document.head.appendChild(metaRobots);
    }
    return () => {
      if (metaRobots.parentNode) {
        metaRobots.remove();
      }
    };
  }, []);

  const onAvatarChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!isAcceptedPhoto(f)) {
      toast.error(t('register.toastInvalidImage'));
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error(t('register.toastImageTooBig'));
      return;
    }
    setAvatarFile(f);
    setAvatarPreview(URL.createObjectURL(f));
  };

  const uploadAvatar = async () => {
    if (!avatarFile) return null;
    const fd = new FormData();
    fd.append('file', avatarFile);
    fd.append('purpose', 'avatar'); // public photo: optimised by the server
    try {
      const { data } = await axios.post(`${API}/upload`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data.url;
    } catch (e) {
      toast.error(translateServerError(e?.response?.data?.detail) || t('register.toastUploadFailed'));
      return null;
    }
  };

  const persistOptionalProfile = async () => {
    // Only voyageur profile gets WhatsApp/Avatar here; providers finish this
    // during the onboarding form (which already exposes those fields).
    if (role !== 'client') return;
    if (!whatsapp.trim() && !avatarFile) return;
    const payload = {};
    if (whatsapp.trim()) {
      payload.whatsapp = whatsapp.trim();
      payload.phone = whatsapp.trim();
    }
    const avatarUrl = await uploadAvatar();
    if (avatarUrl) payload.avatar_url = avatarUrl;
    if (Object.keys(payload).length === 0) return;
    try {
      await axios.put(`${API}/users/me/profile`, payload, { withCredentials: true });
    } catch {
      // Non-blocking — user is already registered, they can retry from the dashboard.
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await register(email, password, name, role);

    if (result.success) {
      await persistOptionalProfile();
      setLoading(false);
      navigate(redirect || '/dashboard', { replace: true });
    } else {
      setLoading(false);
      setError(result.error);
    }
  };

  const isClient = role === 'client';

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-12" data-testid="register-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-block mb-6">
            <Logo />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-primary mb-2">{t('register.pageTitle')}</h1>
          <p className="text-base text-ink-light">{t('register.pageSubtitle')}</p>
        </div>

        <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-card border border-slate-100">
          {error && (
            <Alert variant="destructive" className="mb-6" data-testid="error-alert">
              <AlertDescription>{t(error)}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label className="text-sm font-medium text-primary mb-3 block">{t('register.roleLabel')}</Label>
              <RadioGroup value={role} onValueChange={setRole} className="grid grid-cols-2 gap-4" data-testid="role-selector">
                <div>
                  <RadioGroupItem value="client" id="client" className="peer sr-only" />
                  <Label
                    htmlFor="client"
                    className="flex flex-col items-center justify-center rounded-lg border-2 border-slate-200 bg-white p-4 hover:bg-slate-50 peer-data-[state=checked]:border-accent peer-data-[state=checked]:bg-accent/5 cursor-pointer transition-all"
                    data-testid="role-client"
                  >
                    <span className="text-sm font-medium">{t('register.roleClient')}</span>
                  </Label>
                </div>
                <div>
                  <RadioGroupItem value="provider" id="provider" className="peer sr-only" />
                  <Label
                    htmlFor="provider"
                    className="flex flex-col items-center justify-center rounded-lg border-2 border-slate-200 bg-white p-4 hover:bg-slate-50 peer-data-[state=checked]:border-accent peer-data-[state=checked]:bg-accent/5 cursor-pointer transition-all"
                    data-testid="role-provider"
                  >
                    <span className="text-sm font-medium">{t('register.roleProvider')}</span>
                  </Label>
                </div>
              </RadioGroup>
            </div>

            <div>
              <Label htmlFor="name" className="text-sm font-medium text-primary mb-2 block">{t('register.fieldName')}</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('register.fieldNamePlaceholder')}
                  className="input-field pl-10"
                  required
                  data-testid="name-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email" className="text-sm font-medium text-primary mb-2 block">{t('register.fieldEmail')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('register.fieldEmailPlaceholder')}
                  className="input-field pl-10"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-sm font-medium text-primary mb-2 block">{t('register.fieldPassword')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="input-field pl-10"
                  required
                  minLength="8"
                  data-testid="password-input"
                />
              </div>
            </div>

            {isClient && (
              <>
                <div>
                  <Label htmlFor="whatsapp" className="text-sm font-medium text-primary mb-2 block">
                    {t('register.fieldWhatsapp')} <span className="text-xs text-ink-light font-normal">{t('register.optionalLabel')}</span>
                  </Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="whatsapp"
                      type="tel"
                      value={whatsapp}
                      onChange={(e) => setWhatsapp(e.target.value)}
                      placeholder={t('register.whatsappPlaceholder')}
                      className="input-field pl-10"
                      data-testid="whatsapp-input"
                    />
                  </div>
                  <p className="text-xs text-ink-light mt-1">{t('register.whatsappHelp')}</p>
                </div>

                <div>
                  <Label className="text-sm font-medium text-primary mb-2 block">
                    {t('register.avatarLabel')} <span className="text-xs text-ink-light font-normal">{t('register.optionalLabel')}</span>
                  </Label>
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center shrink-0" data-testid="avatar-preview">
                      {avatarPreview ? (
                        <img src={avatarPreview} alt={t('register.avatarPreviewAlt')} className="w-full h-full object-cover" />
                      ) : (
                        <Camera className="w-6 h-6 text-slate-400" />
                      )}
                    </div>
                    <div className="flex-1">
                      <input
                        ref={avatarInputRef}
                        type="file"
                        accept={PHOTO_ACCEPT}
                        onChange={onAvatarChange}
                        className="hidden"
                        data-testid="avatar-file-input"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        className="text-xs w-full"
                        onClick={() => avatarInputRef.current?.click()}
                        data-testid="avatar-choose-btn"
                      >
                        {avatarFile ? t('register.avatarChange') : t('register.avatarChoose')}
                      </Button>
                      <p className="text-xs text-ink-light mt-1">{t('register.avatarHelp')}</p>
                    </div>
                  </div>
                </div>
              </>
            )}

            <Button type="submit" className="btn-primary w-full" disabled={loading} data-testid="register-submit-button">
              {loading ? t('register.submitLoading') : t('register.submitButton')}
            </Button>
          </form>

          <div className="my-6 relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-slate-500">{t('register.orContinueWith')}</span>
            </div>
          </div>

          <Button
            type="button"
            onClick={() => { rememberAuthRedirect(redirect); googleLogin(role); }}
            className="w-full border-2 border-slate-200 bg-white text-primary hover:bg-slate-50 rounded-full px-6 py-3 font-medium transition-colors flex items-center justify-center"
            data-testid="google-register-button"
          >
            <Chrome className="w-5 h-5 mr-2" />
            {t('register.googleButton')}
          </Button>

          {isClient && (
            <p className="text-xs text-ink-light text-center mt-3">
              {t('register.googleHelpClient')}
            </p>
          )}

          <div className="mt-6 text-center">
            <p className="text-sm text-ink-light">
              {t('register.alreadyRegistered')}{' '}
              <Link to={withRedirect('/login', redirect)} className="inline-flex items-center min-h-11 text-accent hover:text-accent-hover font-semibold" data-testid="login-link">
                {t('register.loginLink')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
